/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { FC, useState, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Steps } from "grab-ui"
import { Input } from "antd"

import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import {
  WithMenuHeader,
  WithMenuHeaderInjectedProps,
} from "components/hoc/WithMenuHeader"
import { CREATE_CATALOGUE, CREATE_MENU, MENUS } from "constants/routes"

import { ConfirmModalV2 } from "components/modals"
import { AppState } from "store/store.type"
import { useHistory, useLocation, useParams } from "react-router"
import { MoreInfoFilled } from "@duxton-web/icons"
import { customStepper } from "styles/common"
import { theme } from "styles/theme"
import { MerchantStoreInfo } from "types/models/menu"
import { actionCreator } from "store/store"
import { AxiosAction } from "types/api"
import { AnyAction } from "redux"
import { CreateMenuGroupTrackerEntryPoint } from "constants/menu_group"
import { useTranslationsMap } from "common/utils/i18n"
import { Config } from "constants/config"
import { waitMenuGroupPropagete } from "utils/menu_group"
import notification from "components/common/notification"
import { storageHelper } from "utils/storage"
import { getResponseErrorEventLabels, TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { menuEditorTabKeys, menuTabEventCategories } from "constants/menu"
import { getGrabXValueFromLocalStorage, MerchantPortalFeatureFlag } from "grabx"
import { createMenuContainer, stepContentContainer } from "./style"
import { StepsAction } from "./StepsAction"
import { SelectStores, SelectMenu, ReviewPublish } from "./steps"
import { useIsMartMenuGroup } from "../useIsMartMenuGroup"

export const CreateMenuGroup: FC = () => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()

  const {
    outlets,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
  } = useSelector((state: AppState) => state.menu)
  const { menuParentID, tabKey } = useParams<{
    menuParentID: string
    tabKey: string
  }>()
  const isMartMenuGroup = useIsMartMenuGroup()
  const { maxLengthMenuGroupName } = Config
  const activeTabKey = tabKey || menuEditorTabKeys.menuOverview

  const [currentStep, setCurrentStep] = useState(0)
  const [menuGroupName, setMenuGroupName] = useState("")
  const [menuGroupNameError, setMenuGroupNameError] = useState("")
  const [selectedStoreIDs, setSelectedStoreIDs] = useState<string[]>([])
  const [selectedStores, setSelectedStores] = useState<MerchantStoreInfo[]>([])
  const [showConfirmReset, toggleConfirmReset] = useState(false)
  const [showConfirmPublish, toggleConfirmPublish] = useState(false)
  const [entryPoint, setEntryPoint] = useState<
    CreateMenuGroupTrackerEntryPoint
  >("")

  const searchInputRef = useRef<Input>(null)

  const textAsBizType = isMartMenuGroup ? "forCatalogue" : "forMenu"

  const linkStoreMaxLimit = getGrabXValueFromLocalStorage<number>(
    MerchantPortalFeatureFlag.linkStoreMaxLimit,
  )

  const textMap = useTranslationsMapTyped({
    resetMessage: "menu.menuGroup.confirm.reset.message",
    resetDescription: "menu.menuGroup.confirm.reset.description",
    resetOkText: "menu.menuGroup.confirm.button.reset",
    publishMessage: `menu.menuGroup.create.confirmPublish.${textAsBizType}.title`,
    publishDescription: `menu.menuGroup.create.confirmPublish.${textAsBizType}.confirmText`,
    publishOkText: "menu.menuGroup.create.confirmPublish.yesPublish",
    noSelectedStore: "menu.menuGroup.create.selectAtLeastOneStore",
    xStoresSelected: "menu.menuGroup.create.xStoreSelected",
    maxStoreSelectedMessage: "menu.menuGroup.message.maxStoresSelected",
    maxLengthNameMessage: "menu.menuGroup.menuName.charLengthExceeded",
    invalidBusinessType: `menu.menuGroup.message.invalidBusinessType`,
    invalidMenuType: `menu.menuGroup.message.${textAsBizType}.invalidMenuType`,
    invalidTimezone: `menu.menuGroup.message.invalidTimezone`,
    selectStore: "menu.menuGroup.create.tab.selectStore",
    selectMenu: `menu.menuGroup.create.tab.${textAsBizType}.selectMenu`,
    reviewAndPublish: "menu.menuGroup.create.tab.reviewAndPublish",
    success: "common.success",
    errorRequired: "menu.errors.requiredField",
  })

  const steps = [
    {
      title: textMap.selectStore,
      content: (
        <SelectStores
          data-testid="select-stores"
          menuGroupName={menuGroupName}
          menuGroupNameError={menuGroupNameError}
          selectedStoreIDs={selectedStoreIDs}
          onChangeMenuGroupName={setMenuGroupName}
          onChangeSelectedStoreIDs={setSelectedStoreIDs}
          searchInputRef={searchInputRef}
        />
      ),
    },
    {
      title: textMap.selectMenu,
      content: (
        <SelectMenu
          data-testid="select-menu"
          selectedStores={selectedStores}
          menuGroupName={menuGroupName}
          entryPoint={entryPoint}
          onChangeEntryPoint={newEntryPoint => setEntryPoint(newEntryPoint)}
        />
      ),
    },
    {
      title: textMap.reviewAndPublish,
      content: <ReviewPublish selectedStores={selectedStores} />,
    },
  ]

  useEffect(() => {
    const showMenuGroupBanner = storageHelper.getShowMenuGroupBanner()
    if (showMenuGroupBanner !== false) {
      storageHelper.setShowMenuGroupBanner(false)
    }
  }, [])

  useEffect(() => {
    setSelectedStores(
      outlets?.filter(outlet => selectedStoreIDs.includes(outlet.merchantID)) ||
        [],
    )
  }, [selectedStoreIDs, outlets])

  // Redirect to food/menu if user directly access using menu group id
  useEffect(() => {
    if (menuParentID && menuParentID !== menuGroupID) {
      history.replace(isMartMenuGroup ? CREATE_CATALOGUE : CREATE_MENU)
    }
  }, [menuParentID, menuGroupID, isMartMenuGroup, history])

  // Fix pathname
  useEffect(() => {
    const currPathName = location.pathname
    if (isMartMenuGroup && currPathName.includes(CREATE_MENU)) {
      history.replace(currPathName.replace(CREATE_MENU, CREATE_CATALOGUE))
    } else if (!isMartMenuGroup && currPathName.includes(CREATE_CATALOGUE)) {
      history.replace(currPathName.replace(CREATE_CATALOGUE, CREATE_MENU))
    }
  }, [history, isMartMenuGroup, location.pathname])

  useEffect(() => {
    if (
      menuGroupName.length > Config.maxLengthMenuGroupName &&
      !menuGroupNameError
    ) {
      setMenuGroupNameError(
        textMap.maxLengthNameMessage.replace(
          "<x>",
          maxLengthMenuGroupName.toString(),
        ),
      )
    } else if (
      menuGroupName.length < Config.maxLengthMenuGroupName &&
      menuGroupNameError
    ) {
      setMenuGroupNameError("")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuGroupName])

  const isMenuGroupCreated = !!menuParentID

  const handleClickPublish = async () => {
    const clickPublishTimestamp = new Date().valueOf()

    if (menuGroupID) {
      const t0 = performance.now()
      dispatch(actionCreator.global.showLoading())
      const resp = (await dispatch(
        actionCreator.menu.publishMenuGroup({
          merchantGroupID,
          menuGroupID,
          linkedStoreIDs: selectedStoreIDs,
        }) as AnyAction,
      )) as AxiosAction<{ menuGroupID: string }>

      let labels = {
        MERCHANT_GROUP_ID: merchantGroupID,
        MENU_GROUP_ID: menuGroupID,
        LOADING_STATUS: "SUCCESS",
        LOAD_TIME: 0,
        REASON: "",
        ERROR_MSG: "",
        CLICK_PUBLISH_TIMESTAMP: clickPublishTimestamp,
        PUBLISH_SHOWN_TIMESTAMP: null as any,
      }
      if (resp?.type.endsWith("_SUCCESS") && resp.payload.data.menuGroupID) {
        await waitMenuGroupPropagete({ menuGroupID, merchantGroupID, dispatch })
        notification.success({
          message: textMap.success,
        })
        history.push(MENUS)
        labels = {
          ...labels,
          PUBLISH_SHOWN_TIMESTAMP: new Date().valueOf(),
        }
      } else if (resp?.type?.endsWith("_FAIL")) {
        labels = {
          ...labels,
          LOADING_STATUS: "FAILURE",
          ...getResponseErrorEventLabels(resp),
        }
      }
      const t1 = performance.now()
      labels = {
        ...labels,
        LOAD_TIME: t1 - t0,
      }
      TroyWebTracker.sendEvent({
        category: EventCategory.menuGroupCreationReview,
        action: EventAction.clickConfirmPublish,
        labels,
      })

      dispatch(actionCreator.global.hideLoading())
    }
  }

  const handleClickReset = async () => {
    if (menuGroupID) {
      const t0 = performance.now()
      dispatch(actionCreator.global.showLoading())
      const resp = (await dispatch(
        actionCreator.menu.resetMenuGroup({
          merchantGroupID,
          menuGroupID,
        }) as AnyAction,
      )) as AxiosAction<{}>

      let labels = {
        MERCHANT_GROUP_ID: merchantGroupID,
        MENU_GROUP_ID: menuGroupID,
        LOADING_STATUS: "SUCCESS",
        LOAD_TIME: 0,
        REASON: "",
        ERROR_MSG: "",
      }
      if (resp?.type.endsWith("_SUCCESS")) {
        setCurrentStep(0)
        toggleConfirmReset(false)
        history.replace(isMartMenuGroup ? CREATE_CATALOGUE : CREATE_MENU)
      } else if (resp?.type?.endsWith("_FAIL")) {
        labels = {
          ...labels,
          LOADING_STATUS: "FAILURE",
          ...getResponseErrorEventLabels(resp),
        }
      }
      const t1 = performance.now()
      labels = {
        ...labels,
        LOAD_TIME: t1 - t0,
      }
      TroyWebTracker.sendEvent({
        category: EventCategory.menuGroupCreationReview,
        action: EventAction.clickConfirmReset,
        labels,
      })

      dispatch(actionCreator.global.hideLoading())
    }
  }

  const onNext = () => {
    if (currentStep === 0) {
      if (!menuGroupName) {
        searchInputRef.current?.input?.focus()
        setMenuGroupNameError(textMap.errorRequired)
        return
      }
      // step 1 to step 2
      TroyWebTracker.sendEvent({
        category: EventCategory.menuGroupCreationSelectStores,
        action: EventAction.clickNext,
        labels: {
          MERCHANT_GROUP_ID: merchantGroupID,
          MENU_GROUP_NAME: menuGroupName,
          NUM_STORES_SELECTED: selectedStores.length,
        },
      })
    }
    if (currentStep === 1) {
      // step 2 to step 3
      TroyWebTracker.sendEvent({
        category: menuTabEventCategories[activeTabKey],
        action: EventAction.clickNext,
        labels: {
          MERCHANT_GROUP_ID: merchantGroupID,
          MENU_GROUP_NAME: menuGroupName,
          NUM_STORES_SELECTED: selectedStores.length,
          ENTRY_POINT: entryPoint,
        },
      })
    }
    setCurrentStep(currentStep + 1)
  }

  const onPrev = () => {
    if (currentStep === 1) {
      // step 2 to step 1
      TroyWebTracker.sendEvent({
        category: EventCategory.menuGroupCreationSelectMenu,
        action: EventAction.clickPrevious,
        labels: {
          MERCHANT_GROUP_ID: merchantGroupID,
        },
      })
    }
    setCurrentStep(currentStep - 1)
  }

  const onChangeStep = (step: number) => {
    if (step === 0 && isMenuGroupCreated) {
      return
    }
    setCurrentStep(step)
  }

  const onReset = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuGroupCreationReview,
      action: EventAction.clickReset,
      labels: {
        MENU_GROUP_ID: menuGroupID,
      },
    })
    toggleConfirmReset(true)
  }

  const onSubmit = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuGroupCreationReview,
      action: EventAction.clickPublish,
      labels: {
        MENU_GROUP_ID: menuGroupID,
      },
    })
    toggleConfirmPublish(true)
  }

  const getStepActionMessage = () => {
    if (currentStep === 0) {
      if (selectedStoreIDs.length) {
        return textMap.xStoresSelected
          .replace("<x>", `${selectedStoreIDs.length}/${linkStoreMaxLimit}`)
          .toUpperCase()
      }
      return textMap.noSelectedStore.toUpperCase()
    }
    return undefined
  }

  const getStepActionErrorMessage = () => {
    if (selectedStoreIDs.length > linkStoreMaxLimit) {
      return textMap.maxStoreSelectedMessage.replace(
        "<x>",
        `${linkStoreMaxLimit}`,
      )
    }
    const selectedBusinessTypes = [
      ...new Set(
        selectedStores.map(selectedStore => selectedStore.businessType),
      ),
    ]
    if (selectedBusinessTypes.length > 1) {
      return textMap.invalidBusinessType
    }
    const selectedMenuTypes = [
      ...new Set(
        selectedStores.map(selectedStore => selectedStore.menuDisplayOption),
      ),
    ]
    if (selectedMenuTypes.length > 1) {
      return textMap.invalidMenuType
    }
    const selectedTimezones = [
      ...new Set(selectedStores.map(selectedStore => selectedStore.timezone)),
    ]
    if (selectedTimezones.length > 1) {
      return textMap.invalidTimezone
    }
    return ""
  }

  const getDisabledNext = () => {
    if (currentStep >= steps.length - 1) return true
    if (getStepActionErrorMessage()) return true
    if (currentStep === 0) {
      return !!menuGroupNameError || selectedStoreIDs.length === 0
    }
    if (currentStep === 1) {
      return !isMenuGroupCreated
    }
    return false
  }

  return (
    <div css={createMenuContainer}>
      <Steps
        css={css`
          ${customStepper}
          ${currentStep === 1 && isMenuGroupCreated
            ? css`
                margin-top: 32px;
              `
            : css`
                margin: 32px 0;
              `}
        `}
        current={currentStep}
        onChange={onChangeStep}
      >
        {steps.map((step, index) => (
          <Steps.Step
            data-testid={`step-${index}`}
            key={step.title}
            title={step.title}
            onClick={() => onChangeStep(index)}
          />
        ))}
      </Steps>
      <div css={stepContentContainer}>{steps[currentStep]?.content}</div>
      <StepsAction
        data-testid="steps-action"
        message={getStepActionMessage()}
        disabledNext={getDisabledNext()}
        disabledPrev={
          currentStep === 0 || (currentStep === 1 && isMenuGroupCreated)
        }
        showSubmit={currentStep === steps.length - 1}
        showReset={isMenuGroupCreated && currentStep === 1}
        showNext={currentStep < steps.length - 1}
        showPrev={currentStep > 0}
        onNext={onNext}
        onPrev={onPrev}
        onReset={onReset}
        onSubmit={onSubmit}
        errorMessage={getStepActionErrorMessage()}
      />
      <ConfirmModalV2
        data-testid="reset-confirm-modal"
        icon={
          <MoreInfoFilled style={{ color: theme.colors.error, fontSize: 36 }} />
        }
        open={showConfirmReset}
        onCancel={() => {
          toggleConfirmReset(false)
        }}
        onOk={handleClickReset}
        message={textMap.resetMessage}
        description={textMap.resetDescription}
        okText={textMap.resetOkText}
      />
      <ConfirmModalV2
        data-testid="publish-confirm-modal"
        icon={
          <MoreInfoFilled
            style={{ color: theme.colors.warning, fontSize: 36 }}
          />
        }
        open={showConfirmPublish}
        onCancel={() => {
          toggleConfirmPublish(false)
        }}
        onOk={handleClickPublish}
        message={textMap.publishMessage.replace(
          "$storeCount",
          `${selectedStoreIDs.length}`,
        )}
        description={textMap.publishDescription}
        okText={textMap.publishOkText}
      />
    </div>
  )
}

export const CreateMenuGroupWithHeaderWrapped = ({
  setHeaderTitle,
}: WithMenuHeaderInjectedProps) => {
  const isMartMenuGroup = useIsMartMenuGroup()
  const dispatch = useDispatch()

  const text = useTranslationsMap({
    title: `menu.menuGroup.create.${
      isMartMenuGroup ? "forCatalogue" : "forMenu"
    }.createNewMenuTitle`,
  })

  useEffect(() => {
    setHeaderTitle(text.title)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMartMenuGroup, text.title])

  // unmount
  useEffect(
    () => () => {
      dispatch(actionCreator.menu.clearMenuGroup())
    },
    [],
  )

  return <CreateMenuGroup />
}

export const CreateMenuGroupWithHeader = WithMenuHeader({ backLink: MENUS })(
  CreateMenuGroupWithHeaderWrapped,
)
